/**
 * Ideas API — клиент народного роадмапа Рублокса.
 *
 * Бэкенд: storys-микросервис, /api-storys/kubikon3d/ideas/*
 * См. план: 1 - Неделя 4/2 - Народный роадмап/PLAN.md
 *
 * Авторизация — сырой JWT в заголовке Authorization (без префикса Bearer).
 * Гость может читать все GET; для POST/like/comment нужен токен +
 * подтверждённый email (бэк сам вернёт 403 email_not_confirmed).
 */
import { request } from "./client";

const PREFIX = "/api-storys/kubikon3d/ideas";
const ADMIN = "/api-storys/kubikon3d/admin/ideas";

/* ── публичное чтение (гость может) ────────────────────────────── */

/**
 * Список идей с фильтрами/сортировкой.
 * params: { category?, status?, sort?: 'hot'|'new'|'top'|'in_progress',
 *           q?, page?, limit? }
 */
export function listIdeas(params = {}, { signal } = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== "") qs.set(k, v);
  });
  const tail = qs.toString();
  return request(`${PREFIX}${tail ? "?" + tail : ""}`, {
    auth: true,        // токен ОПЦИОНАЛЕН: backend поймёт is_liked для своих
    signal,
  });
}

/** 4 секции для главной /ideas. */
export function getSections({ signal } = {}) {
  return request(`${PREFIX}/sections`, { auth: true, signal });
}

/** Карточка идеи + история статусов. */
export function getIdea(id, { signal } = {}) {
  return request(`${PREFIX}/${id}`, { auth: true, signal });
}

/** Комментарии идеи (пагинированно). */
export function getComments(id, { page = 1, limit = 50, signal } = {}) {
  return request(`${PREFIX}/${id}/comments?page=${page}&limit=${limit}`, {
    auth: true,
    signal,
  });
}

/** Лидерборд авторов: period = year | month | all */
export function getLeaderboard({ period = "year", limit = 10, signal } = {}) {
  return request(`${PREFIX}/leaderboard?period=${period}&limit=${limit}`, {
    signal,
  });
}

/* ── требуется логин + подтверждённый email ─────────────────────── */

/** Создать идею. payload: {title, description, category, source_url?} */
export function createIdea(payload) {
  return request(PREFIX, { method: "POST", body: payload, auth: true });
}

/** Лайк/убрать лайк идеи (toggle). */
export function toggleLike(id) {
  return request(`${PREFIX}/${id}/like`, { method: "POST", auth: true });
}

/** Создать комментарий. */
export function createComment(id, text) {
  return request(`${PREFIX}/${id}/comments`, {
    method: "POST",
    body: { text },
    auth: true,
  });
}

/** Лайк/убрать лайк коммента. */
export function toggleCommentLike(cid) {
  return request(`${PREFIX}/comments/${cid}/like`, {
    method: "POST",
    auth: true,
  });
}

/** Дубль-детектор: похожие идеи по title. */
export function checkDuplicates(title, { signal } = {}) {
  return request(
    `${PREFIX}/check-duplicates?title=${encodeURIComponent(title)}`,
    { auth: true, signal }
  );
}

/** Мои идеи (любого статуса). */
export function getMyIdeas({ signal } = {}) {
  return request(`${PREFIX}/my`, { auth: true, signal });
}

/* ── админ (МИН) ────────────────────────────────────────────────── */

/** Сменить статус идеи. */
export function adminChangeStatus(id, payload) {
  return request(`${ADMIN}/${id}/status`, {
    method: "POST",
    body: payload,
    auth: true,
  });
}

/** Скрыть/показать коммент. */
export function adminHideComment(cid, { hide = true, reason } = {}) {
  return request(`${ADMIN}/comments/${cid}/hide`, {
    method: "POST",
    body: { hide, reason },
    auth: true,
  });
}

/** Дашборд статистики. */
export function adminStats({ signal } = {}) {
  return request(`${ADMIN}/stats`, { auth: true, signal });
}

/* ── константы для UI ───────────────────────────────────────────── */

export const CATEGORIES = [
  { id: "games", label: "Игры" },
  { id: "studio", label: "Студия" },
  { id: "skins_shop", label: "Скины и шопы" },
  { id: "platform", label: "Платформа" },
  { id: "gameplay", label: "Геймплей" },
  { id: "mobile", label: "Мобильное приложение" },
  { id: "other", label: "Другое" },
];

export const STATUSES = [
  { id: "voting", label: "На голосовании", color: "#6b7280" },
  { id: "accepted", label: "Принята", color: "#3357ff" },
  { id: "in_progress", label: "В разработке", color: "#7c3aed" },
  { id: "testing", label: "На тестировании", color: "#f59e0b" },
  { id: "done", label: "Сделано", color: "#10b981" },
  { id: "rejected", label: "Отклонена", color: "#dc2626" },
  { id: "frozen", label: "Заморожена", color: "#94a3b8" },
];

export function categoryLabel(id) {
  return CATEGORIES.find((c) => c.id === id)?.label || id;
}

export function statusInfo(id) {
  return STATUSES.find((s) => s.id === id) || { id, label: id, color: "#6b7280" };
}
